'use client';

import { useParams, usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { locales, localeNames, type Locale } from '@/i18n';

// framer-motion replaced with CSS grid-template-rows transition — see Header.tsx
// header for rationale (1040ms processingMs per click on 20× CPU).

type Props = { dark?: boolean };

export function LanguageSelector({ dark = false }: Props) {
  const params = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);

  const currentLocale = (params.locale as Locale) || 'en';

  const switchLanguage = (newLocale: Locale) => {
    const segments = pathname.split('/');
    segments[1] = newLocale;
    router.push(segments.join('/'));
    setIsOpen(false);
  };

  const triggerClass = dark
    ? 'flex w-full items-center justify-between gap-2 text-font-primary hover:opacity-60 transition-opacity xl:w-auto xl:justify-start'
    : 'flex w-full items-center justify-between gap-2 text-font-secondary hover:opacity-70 transition-opacity xl:w-auto xl:justify-start';
  const menuClass = dark
    ? 'border-0 shadow-none bg-transparent xl:bg-[#030b24]/70 xl:backdrop-blur-md xl:border xl:border-white/10'
    : 'border-0 shadow-none bg-transparent xl:bg-white/95 xl:backdrop-blur-md xl:border xl:border-black/[0.08] xl:shadow-[0_1px_12px_0_rgba(0,0,0,0.09)]';

  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className={triggerClass}>
        <span className="uppercase text-nav tracking-[var(--nav-link-tracking)]">{currentLocale}</span>
        <img
          src="/icons/arrow-down.svg"
          alt=""
          className={`size-4 ${dark ? 'invert' : ''} transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-40 hidden xl:block"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        aria-hidden={!isOpen}
        className={`mt-3 w-full grid transition-[grid-template-rows,opacity] duration-150 ease-out z-50 xl:absolute xl:right-0 xl:top-[calc(100%+(var(--nav-height)-100%)/2)] xl:mt-0 xl:w-44 ${
          isOpen
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0 pointer-events-none'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className={`rounded-none xl:rounded-[var(--border-radius-primary)] ${menuClass}`}>
            {locales.map((locale) => (
              <button
                key={locale}
                onClick={() => switchLanguage(locale)}
                tabIndex={isOpen ? 0 : -1}
                className={`w-full flex items-center justify-between px-4 py-3 text-left text-nav tracking-[var(--nav-link-tracking)] transition-colors ${
                  dark
                    ? 'text-font-primary hover:bg-white/5'
                    : 'text-font-secondary hover:bg-black/[0.03]'
                }`}
              >
                {localeNames[locale]}
                {locale === currentLocale && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src="/icons/check.svg"
                    alt=""
                    aria-hidden="true"
                    className={`size-4 ${dark ? 'invert' : ''}`}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
